import { useContext, useEffect } from "react";
import { StateContext } from "../state";
import { get } from "lodash/object";
import { applyCssFromJson, removeStyleNodeByDataUpdatedAt } from "./css";
import moment from "moment";
import { logger } from "./logging";
import { useTranslation } from "react-i18next";

const checkVisibility = (el) => {
  if (!el) {
    return false;
  }

  const {
    top,
    left,
  } = el.getBoundingClientRect();

  const topElement = document.elementFromPoint(left, top);
  return el.isSameNode(topElement);
};

export const useCustomCSS = (ref, namespace) => {
  // this custom hook can be used to dynamically apply CSS based on the program configs and the currently visible component
  // when the component becomes visible, the styles will be applied and appended to the DOM. This enables us to be able to delay applying styles until the component is visible.
  // For instance, if a modal is displayed and is obscuring the underlying component, we don't want to apply header styles that will affect the layout of the modal.
  // When the component unmounts any custom CSS that is applied to the DOM will be removed using the associated data-updated-at attribute that is added when the styles are applied
  const [{configuration}] = useContext(StateContext);
  const transformedCss = get(configuration, `${namespace ? namespace + '.' : ''}css`, get(configuration, 'css'));

  useEffect(() => {
    let updatedAt;
    if(checkVisibility(ref.current)){
      if(transformedCss){
        updatedAt = moment().format('YYYY-MM-DD-mm.SSS');
        applyCssFromJson(transformedCss, namespace, updatedAt);
      }
    }

    const domObserver = new MutationObserver(() => {
      if(checkVisibility(ref.current)){
        if(transformedCss && !updatedAt){
          updatedAt = moment().format('YYYY-MM-DD-mm.SSS');
          applyCssFromJson(transformedCss, namespace, updatedAt);
        }
      } else {
        if(updatedAt){
          const lastUpdated = updatedAt;
          updatedAt = null;
          removeStyleNodeByDataUpdatedAt(lastUpdated);
        }
      }
    });

    domObserver.observe(document.body, {subtree: true, attributes: true, childList: true});

    return () => {
      removeStyleNodeByDataUpdatedAt(updatedAt);
      domObserver.disconnect();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
};

export const useLoggerOnMountEvents = (eventOnOpen, eventOnClose) => {
  const [{configuration, payment}] = useContext(StateContext);
  const [t, i18n] = useTranslation(); // eslint-disable-line no-unused-vars

  useEffect(() => {
    if(eventOnOpen){
      logger({
        event: eventOnOpen,
        language: i18n.language,
        programId: payment.programId,
        programName: configuration.programName,
      });
    }

    return () => {
      if(eventOnClose){
        logger({
          event: eventOnClose,
          language: i18n.language,
          programId: payment.programId,
          programName: configuration.programName,
        });
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
};
